// @ts-nocheck
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useInView } from 'framer-motion';

interface ScrambleTextProps {
  text: string;
  className?: string;
  scrambleSpeed?: number;
  preserveSpace?: boolean;
  as?: React.ElementType;
  delay?: number;
  step?: number;
}

const CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";

const ScrambleText: React.FC<ScrambleTextProps> = ({
  text,
  className = "",
  scrambleSpeed = 40, // Slower update frequency for performance
  preserveSpace = true,
  as: Component = 'span',
  delay = 0,
  step = 3 // Reveal more characters per step
}) => {
  const [displayedText, setDisplayedText] = useState(text);
  const targetTextRef = useRef(text);

  const requestRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<number>(0);
  const iterationRef = useRef<number>(0);

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10% 0px" });
  const hasStarted = useRef(false);

  useEffect(() => {
    targetTextRef.current = text;
    if (isInView && hasStarted.current) {
      iterationRef.current = 0;
      startAnimation();
    }
  }, [text]);

  const animate = (time: number) => {
    if (!lastUpdateRef.current) lastUpdateRef.current = time;
    const deltaTime = time - lastUpdateRef.current;

    if (deltaTime >= scrambleSpeed) {
      const currentText = targetTextRef.current;
      const currentIteration = iterationRef.current;

      // Pre-calculate to avoid excessive operations in loop
      const len = currentText.length;
      const progress = Math.floor(currentIteration);

      let nextText = "";
      for (let i = 0; i < len; i++) {
        const char = currentText[i];
        if (preserveSpace && (char === " " || char === "\n")) {
          nextText += char;
          continue;
        }

        if (i < progress) {
          nextText += char;
        } else {
          // Faster random selection
          nextText += CHARS.charAt(Math.floor(Math.random() * CHARS.length));
        }
      }

      setDisplayedText(nextText);

      if (progress >= len) {
        setDisplayedText(currentText); // Ensure final state is correct
        return;
      }

      iterationRef.current += step;
      lastUpdateRef.current = time;
    }

    requestRef.current = requestAnimationFrame(animate);
  };

  const startAnimation = useCallback(() => {
    if (requestRef.current) cancelAnimationFrame(requestRef.current);
    lastUpdateRef.current = 0;
    requestRef.current = requestAnimationFrame(animate);
  }, [scrambleSpeed, step]);

  useEffect(() => {
    if (isInView && !hasStarted.current) {
      hasStarted.current = true;
      const timeout = setTimeout(() => {
        iterationRef.current = 0;
        startAnimation();
      }, delay);
      return () => clearTimeout(timeout);
    }
  }, [isInView, delay, startAnimation]);

  useEffect(() => {
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, []);

  return (
    <Component
      ref={ref}
      className={`relative inline-block align-bottom max-w-full ${className}`}
      style={{
        wordBreak: 'break-word',
        overflowWrap: 'anywhere'
      }}
    >
      <span className="invisible select-none pointer-events-none whitespace-pre-wrap block" aria-hidden="true">
        {text}
      </span>
      <span className="absolute top-0 left-0 w-full h-full text-inherit whitespace-pre-wrap break-words overflow-hidden pointer-events-none">
        {displayedText}
      </span>
    </Component>
  );
};

export default ScrambleText;
